<template>
    <div class="widget widget-flow">
        <div class="widget-flow__inner">
            <div class="widget-flow__content">
                <h3 class="widget-flow__heading">
                    {{ $t('widgetFlow_Heading') }}
                </h3>
                <div class="widget-frequency__error" v-if="hasFetchError" role="alert">
                    {{ $t('widgetFlow_FetchError') }}
                </div>
                <div class="widget-flow__select">
                    <label :for="areaSelectId">
                        {{ $t('widgetFlow_Area') }}
                        <select v-model="selectedAreaId" :id="areaSelectId" class="form-control form-control--select">
                            <option :value="null">
                                {{ $t('widgetFlow_Select') }}
                            </option>
                            <option v-for="area in selectableAreas" :key="area.id" :value="area.id">
                                {{ area.name }} ({{ area.id }})
                            </option>
                        </select>
                    </label>
                </div>
                <div v-if="selectedArea">
                    <div class="widget-flow__price">
                        <p v-if="selectedAreaPrice">
                            {{ $t('widgetFlow_PriceFor') }} {{ selectedArea.name }}: <strong>{{ selectedAreaPrice }} {{ $t('widgetFlow_PriceUnit') }}</strong>
                        </p>
                    </div>
                    <table class="widget-flow__table">
                        <caption class="sr-only">
                            {{ $t('widgetFlow_FlowFor') }} {{ selectedArea.name }}
                        </caption>
                        <tbody>
                            <tr v-for="(row, index) in visibleFlowTable" :key="index">
                                <td>
                                    <div v-if="row.direction === IMPORT" class="widget-flow__arrow widget-flow__arrow--left" aria-hidden="true"></div>
                                    <div v-if="row.direction === EXPORT" class="widget-flow__arrow widget-flow__arrow--right" aria-hidden="true"></div>
                                </td>
                                <td class="widget-flow__cell widget-flow__cell--name">
                                    <strong>
                                        <span v-if="row.direction === IMPORT">
                                            {{ $t('widgetFlow_ImportFrom') }}
                                        </span>
                                        <span v-if="row.direction === NONE">
                                            {{ $t('widgetFlow_ExportImport') }}
                                        </span>
                                        <span v-if="row.direction === EXPORT">
                                            {{ $t('widgetFlow_ExportTo') }}
                                        </span>
                                        <span v-if="row.otherArea">
                                            {{' '}}
                                            {{ row.otherArea.name }}
                                            ({{ row.otherArea.id }})
                                        </span>
                                    </strong>
                                </td>
                                <td class="widget-flow__cell widget-flow__cell--value">
                                    {{ row.formattedValue }} {{ $t('widgetFlow_Unit') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="widget-flow__map">
                <Map :areas="areas"
                     :selectedAreaId="selectedAreaId"
                     :arrows="flowArrows"
                     :clickable="true"
                     @area-click="clickArea" />
            </div>
        </div>
        <div class="widget-flow__link">
            <a :href="widgetUrl">{{ $t('widgetFlow_Link') }}</a>
        </div>
    </div>
</template>

<script setup>
    import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
    import { fetchFlows } from '../service.js';
    import Map from './Map.vue';
    import { getArrowId } from '../helpers/Map.helper.js';

    const OTHER_AREA_ID = 'other countries';
    const IMPORT = 1;
    const NONE = 0;
    const EXPORT = -1;
    const FETCH_INTERVAL = 1000 * 60; // Default fetch interval. 60 seconds
    const MIN_FETCH_INTERVAL = 1000 * 10; // 10 seconds

    const props = defineProps({
        areas: {
            type: Array,
            default: () => [],
        },
        widgetUrl: {
            type: String,
            default: '',
        },
        fetchInterval: {
            type: [Number, String],
            default: null,
        },
    });

    const instance = getCurrentInstance();
    const id = `WidgetFlow-${instance ? instance.uid : 'unknown'}`;
    const areaSelectId = `${id}-area-select`;
    const selectedAreaId = ref(null);
    const interval = ref(null);
    const flows = ref(null);
    const hasFetchError = ref(false);

    /**
     * Selectable areas
     */
    const selectableAreas = computed(() => {
        if(!flows.value) {
            return [];
        }

        return props.areas.filter(area => !!flows.value.find(flow => flow.elspotId === area.id));
    });

    /**
     * Selected area
     */
    const selectedArea = computed(() => {
        return getArea(selectedAreaId.value);
    });

    /**
     * Selected area price
     */
    const selectedAreaPrice = computed(() => {
        if(!selectedAreaId.value || !flows.value) {
            return '';
        }

        const flow = flows.value.find(currentFlow => currentFlow.elspotId === selectedAreaId.value);

        if(!flow) {
            return '';
        }

        return formatPrice(flow.price);
    });

    /**
     * Flow table
     *
     * Sets flow table for currently selected area
     */
    const flowTable = computed(() => {
        return getFlowTable(selectedAreaId.value);
    });

    const visibleFlowTable = computed(() => {
        return flowTable.value.filter(row => row.otherArea && row.otherArea.id !== OTHER_AREA_ID);
    });

    /**
     * Flow tables
     *
     * Sets array of flow tables for all selectable areas
     */
    const flowTables = computed(() => {
        return selectableAreas.value.map(area => {
            return getFlowTable(area.id);
        });
    });

    /**
     * Flow arrows
     *
     * Sets array of arrows that should be visible in <Map />
     */
    const flowArrows = computed(() => {
        let currentFlowTable;

        if(selectedAreaId.value) {
            currentFlowTable = flowTable.value;
        } else {
            currentFlowTable = [].concat.apply([], flowTables.value);
        }

        return currentFlowTable.map(row => {
            switch(row.direction) {
                case IMPORT:
                    return getArrowId(row.otherArea.id, row.area.id);
                case EXPORT:
                    return getArrowId(row.area.id, row.otherArea.id);
            }
        });
    });

    /**
     * Fetch data
     */
    async function fetchData() {
        try {
            hasFetchError.value = false;

            flows.value = await fetchFlows();
        } catch(error) {
            hasFetchError.value = true;
            flows.value = null;

            console.error(error);
        }
    }

    /**
     * Get area
     */
    function getArea(areaId) {
        return props.areas.find(area => area.id === areaId);
    }

    /**
     * Get flow table
     *
     * Returns an array of table rows describing flows between areas.
     */
    function getFlowTable(areaId) {
        if(!areaId || !flows.value) {
            return [];
        }

        const flow = flows.value.find(currentFlow => currentFlow.elspotId === areaId);

        if(!flow) {
            return [];
        }

        const currentFlowTable = flow.incomingFlows.map(item => {
            return {
                area: getArea(areaId),
                otherArea: props.areas.find(area => area.id === item.otherElspotId),
                direction: item.amount > 0 ? IMPORT : NONE,
                value: item.amount,
                formattedValue: formatValue(item.amount),
            };
        }).concat(
            flow.outgoingFlows.map(item => {
                return {
                    area: getArea(areaId),
                    otherArea: props.areas.find(area => area.id === item.otherElspotId),
                    direction: item.amount > 0 ? EXPORT : NONE,
                    value: item.amount,
                    formattedValue: formatValue(item.amount),
                };
            })
        );

        return currentFlowTable.concat(
            getOtherAreaFlowTableRows(areaId, currentFlowTable)
        );
    }

    /**
     * Get other area flow table rows
     *
     * Returns a flow table that describes flow from <area> to non-selectable areas.
     *
     * Used to summarize flow from norwegian areas to/from other countries.
     */
    function getOtherAreaFlowTableRows(areaId, currentFlowTable) {
        const area = getArea(areaId);

        const otherArea = {
            id: OTHER_AREA_ID,
            name: '',
        };

        const imported = { // Other countries to norway
            area,
            otherArea,
            direction: IMPORT,
            amount: 0,
        };

        const exported = { // Norway to other countries
            area,
            otherArea,
            direction: EXPORT,
            amount: 0,
        };

        currentFlowTable.forEach(row => {
            const isOtherAreaSelectable = !!selectableAreas.value.find(selectableArea => selectableArea.id === row.otherArea.id);

            if(!isOtherAreaSelectable) { // Flow to/from area outside of Norway
                if(row.direction === IMPORT) {
                    imported.amount += row.value;
                } else if(row.direction === EXPORT) {
                    exported.amount += row.value;
                }
            }
        });

        imported.value = imported.amount;
        imported.formattedValue = formatValue(imported.amount);

        exported.value = exported.amount;
        exported.formattedValue = formatValue(exported.amount);

        return [imported, exported].filter(row => row.value !== 0);
    }

    /**
     * Click area
     */
    function clickArea(areaId) {
        selectedAreaId.value = areaId;
    }

    onMounted(() => {
        fetchData();

        const nextFetchInterval = props.fetchInterval || FETCH_INTERVAL;

        if(nextFetchInterval < MIN_FETCH_INTERVAL) {
            console.error('Fetch interval is too low:', nextFetchInterval);

            fetchData();
            return;
        }

        interval.value = setInterval(fetchData, nextFetchInterval);
    });

    onUnmounted(() => {
        clearInterval(interval.value);
    });

    function formatValue(value) {
        if (value <= 1) {
            return Math.round(value * 100) / 100;
        }

        return Math.round(value);
    }

    function formatPrice(value) {
        return `${(value + '').replace('.', ',')}`;
    }
</script>
